/**
 * /web/api/profile/2fa/* React Query hooks.
 *
 * Lifecycle:
 *   1. setup     → returns secret + otpauth_url; nothing persisted to DB yet
 *   2. confirm   → activates with the user's first valid code
 *   3. settings  → toggle require_login / require_purchase
 *   4. disable   → wipes the secret (requires a current code)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface TotpStatus {
  enabled: boolean;
  require_login: boolean;
  require_purchase: boolean;
  require_deposit_change: boolean;
}

export interface TotpSetupResponse {
  secret: string;
  otpauth_url: string;
}

export function useTotpStatus() {
  return useQuery<TotpStatus, ApiError>({
    queryKey: ["totp", "status"],
    queryFn: () => api.get<TotpStatus>("/web/api/profile/2fa/status"),
    staleTime: 30_000,
  });
}

export function useTotpSetup() {
  return useMutation<TotpSetupResponse, ApiError, { current_password: string }>({
    mutationFn: (body) =>
      api.post<TotpSetupResponse>("/web/api/profile/2fa/setup", { body }),
  });
}

export function useTotpConfirm() {
  const qc = useQueryClient();
  return useMutation<
    TotpStatus,
    ApiError,
    { code: string; current_password: string }
  >({
    mutationFn: (body) =>
      api.post<TotpStatus>("/web/api/profile/2fa/confirm", { body }),
    onSuccess: (data) => {
      qc.setQueryData(["totp", "status"], data);
      // The user just enabled 2FA — `me` doesn't change but we let
      // any consumers that key off the status see it sooner.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation<TotpStatus, ApiError, { code: string }>({
    mutationFn: (body) =>
      api.post<TotpStatus>("/web/api/profile/2fa/disable", { body }),
    onSuccess: (data) => {
      qc.setQueryData(["totp", "status"], data);
    },
  });
}

export interface TotpSettingsBody {
  require_login?: boolean;
  require_purchase?: boolean;
  require_deposit_change?: boolean;
  /** Current 6-digit TOTP — required by the server to flip toggles. */
  code: string;
}

export function useTotpSettings() {
  const qc = useQueryClient();
  return useMutation<TotpStatus, ApiError, TotpSettingsBody>({
    mutationFn: (body) =>
      api.patch<TotpStatus>("/web/api/profile/2fa/settings", { body }),
    onSuccess: (data) => {
      qc.setQueryData(["totp", "status"], data);
    },
  });
}
